import { MouseEvent, useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";

import { setIsImageUploading } from "redux/SingleDataset/actions";
import XIcon from "assets/icons/XIcon";
import { validateImageUpload } from "helpers/validations";

import ImageUploadModal from "./ImageUploadModal";
import styles from "./style.module.scss";
import { useImageUpload } from "hooks/useImageUpload";

const StyledDownArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="41"
    viewBox="0 0 27 41"
    fill="none"
  >
    <path
      d="M10.5098 34.9314C12.1653 34.2 13.6867 33.5712 14.9846 33.0905C17.5691 32.1177 19.2818 31.7147 19.4625 32.088C19.6432 32.4612 18.3476 33.5765 16.1123 35.0464C14.9946 35.7758 13.5636 36.6082 11.9759 37.4866L9.48239 38.8329L8.14046 39.5178C7.80518 39.6949 7.45279 39.8367 7.08885 39.9409C6.58673 40.0881 6.05796 40.1172 5.54299 40.026C5.03583 39.9258 4.5591 39.7063 4.1513 39.3854C3.9594 39.2336 3.78242 39.0636 3.62281 38.8775C3.50615 38.7406 3.39713 38.5973 3.29628 38.4481C3.00271 37.993 2.73535 37.5211 2.49553 37.0348C2.06841 36.1869 1.69104 35.3144 1.36537 34.4216C0.811436 32.9315 0.429688 31.3817 0.227722 29.803C-0.0960075 27.1188 0.267547 25.4632 0.715621 25.4389C1.16369 25.4145 1.67362 26.9536 2.54516 29.3185C2.98634 30.4499 3.54071 31.8868 4.25214 33.3798L4.60212 34.0923C5.07878 32.7195 5.77803 30.9493 6.82266 28.6566C8.28017 25.486 9.89915 22.3939 11.6731 19.3928C13.625 16.0724 15.7524 12.8608 18.0459 9.77197C22.4159 3.86375 26.6131 -0.298898 26.9728 0.0170149C27.3325 0.332928 24.1122 5.06979 20.4714 11.2132C16.8182 17.332 13.5161 23.6584 10.5816 30.1609C9.35931 32.8735 8.50376 34.7916 7.8478 36.1537L7.91502 36.1534L10.4989 35.0107"
      fill="currentColor"
    />
  </svg>
);

const Header = ({ fileError }: { fileError: boolean }) => {
  const headingContainerClassnames = classNames(styles.headingContainer, {
    [styles.headingContainerWithError]: fileError,
  });

  return (
    <div className={headingContainerClassnames}>
      <StyledDownArrow />
      <div className={styles.headingText}>Drop Image Here</div>
      {fileError && (
        <div className={styles.errorText}>
          Please use a valid image file (JPG, PNG, GIF, WEBP, or BMP)
        </div>
      )}
    </div>
  );
};

const preventDefault = (e: MouseEvent) => e.preventDefault();

const ImageDropBox = () => {
  const { file, setFile, setCropData, cropData, handleVisualSearch } =
    useImageUpload();
  const [fileError, setFileError] = useState(false);

  const inputRef = useRef<any>(null);
  const dispatch = useDispatch();

  const dropBoxContainerClassnames = classNames(styles.imageDropBoxContainer, {
    [styles.errorBorder]: fileError,
  });

  const handleCrossClick = (e: MouseEvent) => {
    e.stopPropagation();

    setFile(null);
    setCropData(null);
    setFileError(false);
    dispatch(setIsImageUploading(false));
  };

  const setSelectedFile = (file: File) => {
    if (validateImageUpload(file)) {
      setFileError(false);
      setFile(file);
    } else {
      setFileError(true);
    }
  };

  const uploadFile = async (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Auto-trigger visual search when crop data is available (restored from old version)
  useEffect(() => {
    if (cropData) {
      handleVisualSearch(cropData);
    }
    //eslint-disable-next-line
  }, [cropData]);

  return (
    <div className={styles.imageDropBoxWrapper}>
      <div
        className={dropBoxContainerClassnames}
        onDragOver={preventDefault}
        onDragLeave={preventDefault}
        onDrop={handleDrop}
      >
        <div
          className={classNames(styles.closeButton, {
            [styles.closeButtonWithError]: fileError,
          })}
          onClick={handleCrossClick}
        >
          <XIcon />
        </div>
        <Header fileError={fileError} />
        <div className={styles.orText}>or</div>
        <div
          className={styles.browseButton}
          onClick={() => inputRef?.current?.click()}
        >
          Browse your computer
        </div>
        <input
          type="file"
          ref={inputRef}
          hidden
          onChange={(e: any) => uploadFile(e)}
        />
      </div>
      {!!file && (
        <ImageUploadModal
          file={file}
          setFile={setFile}
          setCropData={setCropData}
        />
      )}
    </div>
  );
};
export default ImageDropBox;
